import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ColombiaRail,
  ColombiaService,
  LiveRates,
  VesCopConfig,
  VesLiveRates,
  VesPreview,
  VesPreviewItem,
  VesTasaSource,
} from '../../services/colombia.service';

/** Form de la pata VES→USD (singleton — la pata USD→COP se configura aparte). */
interface VesForm {
  activo: boolean;
  tasaSource: VesTasaSource;
  /** Solo MANUAL: tasa VES por USDT. */
  tasaManual: number | null;
  /** Solo MANUAL: vencimiento (datetime-local). */
  tasaManualVigenteHasta: string | null;
  /** Markup ADICIONAL sobre la tasa VES/USDT, en % (encima del ~1% ya incluido). */
  markupPct: number | null;
  /** Mínimo propio en VES (0 = sin piso). */
  minVes: number | null;
  motivo: string;
}

const DEFAULT_FORM: VesForm = {
  activo: false,
  tasaSource: 'AUTO',
  tasaManual: null,
  tasaManualVigenteHasta: null,
  markupPct: 0,
  minVes: 0,
  motivo: '',
};

/** Mínimo del rail (USD), igual que USD→COP. */
const MIN_USD = 20;
/** Montos VES de la tabla de preview (≈ 20 / 65 / 130 / 1300 USD a ~793). */
const PREVIEW_MONTOS_VES = [16000, 50000, 100000, 1000000];

export interface RailVesCopConfigData {
  countryCode?: string; // 'CO'
  countryName?: string;
}

/**
 * MODAL "VES → COP — Configuración" (pata VES→USD).
 *
 * El usuario venezolano paga con su saldo VES y el beneficiario en CO recibe COP
 * — ve UNA sola tasa COP/VES. Acá se gobierna solo la conversión de entrada
 * VES→USD (fuente AUTO=`ves_usdt_rates`/MANUAL + markup + activación); la pata
 * USD→COP (Vita, ESPEJO) se configura en "USD/COP". La tabla de preview usa el
 * mismo motor del backend. Espejo de `RailClpCopConfigComponent`.
 */
@Component({
  selector: 'app-rail-ves-cop-config',
  templateUrl: './rail-ves-cop-config.component.html',
  styleUrls: ['./rail-ves-cop-config.component.sass'],
})
export class RailVesCopConfigComponent implements OnInit, OnDestroy {
  readonly rails: ColombiaRail[] = ['COBREB', 'CO'];
  readonly railLabel: Record<ColombiaRail, string> = {
    COBREB: 'Bre-B (instantáneo)',
    CO: 'Transferencia (1 día)',
  };
  readonly sources: { value: VesTasaSource; label: string; hint: string }[] = [
    { value: 'AUTO', label: 'Tasa Aguacate (vivo)', hint: 'ves_usdt_rates.tasa_final (ya trae ~1%)' },
    { value: 'MANUAL', label: 'Manual (override)', hint: 'Tasa fija con vencimiento' },
  ];

  config: VesCopConfig | null = null;
  live: VesLiveRates | null = null;
  /** Tasas vivas de la pata USD→COP (Vita) — para mostrar la tasa Vita actual. */
  usdLive: LiveRates | null = null;

  form: VesForm = { ...DEFAULT_FORM };

  preview: VesPreview | null = null;
  previewLoading = false;
  private previewTimer: any = null;
  private previewSeq = 0;

  /** USD→COP (master). Si OFF, no se puede ofrecer VES→COP (va por ese puente). */
  usdCopOfrecido = true;
  loading = false;
  saving = false;
  error: string | null = null;
  okMsg: string | null = null;

  constructor(
    private svc: ColombiaService,
    public dialogRef: MatDialogRef<RailVesCopConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RailVesCopConfigData,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const [config, live, usdLive, railes] = await Promise.all([
        this.svc.getVesConfig(),
        this.svc.getVesLive(),
        this.svc.getLive().catch(() => null),
        this.svc.getRailesActivos().catch(() => null),
      ]);
      this.config = config;
      this.live = live;
      this.usdLive = usdLive;
      this.fromConfig(config);
      // El on/off vive en el switchboard (parametros), no en la config de precio.
      this.form.activo = !!railes?.cop?.ves_cop;
      // usd_cop es el MASTER de COP: VES→COP va por el puente USD→COP, así que
      // no se puede ofrecer VES→COP si USD→COP está apagado.
      this.usdCopOfrecido = railes?.cop?.usdt_cop !== false;
      if (!this.usdCopOfrecido) this.form.activo = false;
      this.refreshPreview(true);
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo cargar la configuración';
    } finally {
      this.loading = false;
    }
  }

  private fromConfig(c: VesCopConfig | null): void {
    if (!c) {
      this.form = { ...DEFAULT_FORM };
      return;
    }
    this.form = {
      activo: false, // lo setea load() desde el switchboard
      tasaSource: c.tasaSource,
      tasaManual: c.tasaManual != null ? Number(c.tasaManual) : null,
      tasaManualVigenteHasta: c.tasaManualVigenteHasta
        ? c.tasaManualVigenteHasta.slice(0, 16)
        : null,
      markupPct: c.markupPct != null ? Number(c.markupPct) : 0,
      minVes: c.minVes != null ? Number(c.minVes) : 0,
      motivo: '',
    };
  }

  onFormChange(): void {
    this.okMsg = null;
    this.refreshPreview();
  }

  /** Toggle "Ofrecer VES→COP". Bloqueado si USD→COP (master) está OFF. */
  toggleActivo(): void {
    if (!this.usdCopOfrecido) return;
    this.form.activo = !this.form.activo;
    this.onFormChange();
  }

  refreshPreview(inmediato = false): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.fetchPreview(), inmediato ? 0 : 600);
  }

  private async fetchPreview(): Promise<void> {
    const seq = ++this.previewSeq;
    this.previewLoading = true;
    try {
      const p = await this.svc.vesQuotePreview(PREVIEW_MONTOS_VES, {
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManual != null
            ? Number(this.form.tasaManual)
            : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
        markupPct: this.form.markupPct != null ? Number(this.form.markupPct) : undefined,
        minVes: this.form.minVes != null ? Number(this.form.minVes) : undefined,
      });
      if (seq === this.previewSeq) this.preview = p;
    } catch {
      if (seq === this.previewSeq) this.preview = null;
    } finally {
      if (seq === this.previewSeq) this.previewLoading = false;
    }
  }

  async save(): Promise<void> {
    if (this.form.tasaSource === 'MANUAL') {
      if (!(Number(this.form.tasaManual) > 0) || !this.form.tasaManualVigenteHasta) {
        this.error = 'MANUAL requiere tasa y vencimiento futuros.';
        return;
      }
    }
    this.saving = true;
    this.error = null;
    this.okMsg = null;
    try {
      // 1) On/off del rail (switchboard en parametros).
      await this.svc.setRailesActivos({ vesCop: !!this.form.activo });
      // 2) Config de PRECIO (entity versionada) — sin el on/off.
      await this.svc.upsertVesConfig({
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' ? Number(this.form.tasaManual) : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
        markupPct: this.form.markupPct != null ? Number(this.form.markupPct) : undefined,
        minVes: this.form.minVes != null ? Number(this.form.minVes) : undefined,
        motivo: this.form.motivo || undefined,
      });
      this.okMsg = `Configuración guardada (${this.form.activo ? 'VES→COP activo' : 'VES→COP inactivo'}).`;
      await this.load();
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo guardar';
      await this.load();
    } finally {
      this.saving = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ── Helpers de vista ──

  itemsDe(rail: ColombiaRail): VesPreviewItem[] {
    return this.preview?.rails?.[rail] ?? [];
  }

  /** Tasa VES/USDT que se está usando (de la fuente activa + markup). */
  get tasaVesUsd(): number | null {
    return this.preview?.rate?.efectiva ?? this.live?.rate?.efectiva ?? null;
  }
  get tasaStale(): boolean {
    return !!(this.preview?.rate?.stale ?? this.live?.rate?.stale);
  }
  get sourceResuelta(): VesTasaSource | null {
    return this.preview?.rate?.source ?? this.live?.rate?.source ?? null;
  }

  /** Mínimo (20 USD) expresado en VES a la tasa actual. */
  get minVesEquivalente(): number | null {
    const r = this.tasaVesUsd;
    return r && r > 0 ? Math.ceil(MIN_USD * r) : null;
  }

  // ── Tasa Vita actual (pata de salida USD→COP) ──
  vitaRetailCop(rail: ColombiaRail): number | null {
    return this.usdLive?.rails?.[rail]?.publicSellUsd ?? this.usdLive?.rails?.[rail]?.publicUsdSell ?? null;
  }
  vitaPartnerCop(rail: ColombiaRail): number | null {
    return this.usdLive?.rails?.[rail]?.partnerUsdSell ?? null;
  }
  vitaFeeCop(rail: ColombiaRail): number | null {
    return this.usdLive?.rails?.[rail]?.fixedCostCop ?? null;
  }

  private allItems(): VesPreviewItem[] {
    return this.rails.flatMap((r) => this.itemsDe(r));
  }

  // ── Alertas (espejo de las del modal CLP→COP) ──

  get alertSinMargen(): boolean {
    return this.allItems().some((i) => i.cumpleMinimo && i.disponible && !i.margenOk);
  }
  get alertBajoVita(): boolean {
    return this.allItems().some(
      (i) => i.clamped || (i.ofertaVitaCop != null && i.recibeCop < i.ofertaVitaCop),
    );
  }
  get alertEspejoDegradado(): boolean {
    return this.allItems().some((i) => i.espejoFallback);
  }
  get alertFeedStale(): boolean {
    return this.tasaStale || this.allItems().some((i) => i.stale);
  }
}
